import React from 'react';
import BottomTabNav from '../shared/components/BottomTabNav';
import Footer from '../shared/components/Footer';
import './CustomerPrivacyPolicy.css';

const CustomerPrivacyPolicy = () => {
  return (
    <div className="App">
      <div className="privacy-container">
        <div className="privacy-content">
          {/* Header */}
          <div className="privacy-header">
            <h1 className="privacy-title">Privacy Policy</h1>
            <p className="privacy-subtitle">Your privacy is important to us</p>
            <p className="last-updated">Last updated: January 1, 2024</p>
          </div>

          {/* Introduction */}
          <div className="privacy-section">
            <h2 className="section-title">Introduction</h2>
            <div className="content-card">
              <p>
                Welcome to ZepEat! This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our food delivery service, including our mobile application and website. Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the application or website.
              </p>
            </div>
          </div>

          {/* Information We Collect */}
          <div className="privacy-section">
            <h2 className="section-title">Information We Collect</h2>
            <div className="content-card">
              <div className="info-category">
                <h3>Personal Information</h3>
                <ul>
                  <li>Name, email address, phone number</li>
                  <li>Delivery address and billing information</li>
                  <li>Payment method details (encrypted)</li>
                  <li>Date of birth (for age verification)</li>
                  <li>Profile picture (optional)</li>
                </ul>
              </div>
              
              <div className="info-category">
                <h3>Usage Information</h3>
                <ul>
                  <li>Order history and preferences</li>
                  <li>App usage patterns and interactions</li>
                  <li>Device information (type, operating system, version)</li>
                  <li>IP address and location data</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
              </div>
              
              <div className="info-category">
                <h3>Location Information</h3>
                <ul>
                  <li>GPS location for delivery purposes</li>
                  <li>Address information for order fulfillment</li>
                  <li>Location history for improved service</li>
                </ul>
              </div>
            </div>
          </div>

          {/* How We Use Your Information */}
          <div className="privacy-section">
            <h2 className="section-title">How We Use Your Information</h2>
            <div className="content-card">
              <div className="usage-grid">
                <div className="usage-item">
                  <div className="usage-icon">🛍️</div>
                  <div className="usage-content">
                    <h3>Order Processing</h3>
                    <p>To process and fulfill your food orders, manage payments, and provide customer support.</p>
                  </div>
                </div>
                
                <div className="usage-item">
                  <div className="usage-icon">📍</div>
                  <div className="usage-content">
                    <h3>Delivery Services</h3>
                    <p>To coordinate delivery logistics, track orders, and ensure timely delivery to your location.</p>
                  </div>
                </div>
                
                <div className="usage-item">
                  <div className="usage-icon">✨</div>
                  <div className="usage-content">
                    <h3>Personalization</h3>
                    <p>To customize your experience, recommend restaurants, and show relevant offers based on your preferences.</p>
                  </div>
                </div>
                
                <div className="usage-item">
                  <div className="usage-icon">📧</div>
                  <div className="usage-content">
                    <h3>Communication</h3>
                    <p>To send order updates, promotional offers, and important service notifications.</p>
                  </div>
                </div>
                
                <div className="usage-item">
                  <div className="usage-icon">🛡️</div>
                  <div className="usage-content">
                    <h3>Security</h3>
                    <p>To protect against fraud, ensure account security, and maintain service integrity.</p>
                  </div>
                </div>
                
                <div className="usage-item">
                  <div className="usage-icon">📊</div>
                  <div className="usage-content">
                    <h3>Analytics</h3>
                    <p>To analyze usage patterns, improve our services, and develop new features.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Information Sharing */}
          <div className="privacy-section">
            <h2 className="section-title">Information Sharing</h2>
            <div className="content-card">
              <p>We may share your information in the following circumstances:</p>
              
              <div className="sharing-list">
                <div className="sharing-item">
                  <h3>🏪 With Restaurants</h3>
                  <p>We share necessary order details with partner restaurants to fulfill your orders.</p>
                </div>
                
                <div className="sharing-item">
                  <h3>🚴 With Delivery Partners</h3>
                  <p>We share delivery information with our riders to ensure successful order delivery.</p>
                </div>
                
                <div className="sharing-item">
                  <h3>💳 With Payment Processors</h3>
                  <p>We share payment information with secure payment processors to handle transactions.</p>
                </div>
                
                <div className="sharing-item">
                  <h3>⚖️ Legal Requirements</h3>
                  <p>We may disclose information when required by law or to protect our legal rights.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Data Security */}
          <div className="privacy-section">
            <h2 className="section-title">Data Security</h2>
            <div className="content-card">
              <div className="security-features">
                <div className="security-item">
                  <div className="security-icon">🔐</div>
                  <div className="security-content">
                    <h3>Encryption</h3>
                    <p>All sensitive data is encrypted using industry-standard SSL/TLS protocols.</p>
                  </div>
                </div>
                
                <div className="security-item">
                  <div className="security-icon">🛡️</div>
                  <div className="security-content">
                    <h3>Secure Storage</h3>
                    <p>Your data is stored on secure servers with restricted access and regular security audits.</p>
                  </div>
                </div>
                
                <div className="security-item">
                  <div className="security-icon">🔑</div>
                  <div className="security-content">
                    <h3>Access Control</h3>
                    <p>Only authorized personnel have access to your personal information on a need-to-know basis.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Your Rights */}
          <div className="privacy-section">
            <h2 className="section-title">Your Privacy Rights</h2>
            <div className="content-card">
              <div className="rights-grid">
                <div className="right-item">
                  <h3>📋 Access</h3>
                  <p>Request a copy of the personal information we hold about you.</p>
                </div>
                
                <div className="right-item">
                  <h3>✏️ Correction</h3>
                  <p>Update or correct any inaccurate personal information.</p>
                </div>
                
                <div className="right-item">
                  <h3>🗑️ Deletion</h3>
                  <p>Request deletion of your personal information, subject to legal requirements.</p>
                </div>
                
                <div className="right-item">
                  <h3>⛔ Opt-out</h3>
                  <p>Opt-out of marketing communications and data processing activities.</p>
                </div>
                
                <div className="right-item">
                  <h3>📤 Portability</h3>
                  <p>Request a copy of your data in a portable, machine-readable format.</p>
                </div>
                
                <div className="right-item">
                  <h3>🔒 Restriction</h3>
                  <p>Request restriction of processing under certain circumstances.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Cookies */}
          <div className="privacy-section">
            <h2 className="section-title">Cookies and Tracking</h2>
            <div className="content-card">
              <p>We use cookies and similar tracking technologies to enhance your experience:</p>
              
              <div className="cookie-types">
                <div className="cookie-type">
                  <h3>Essential Cookies</h3>
                  <p>Required for basic functionality like login sessions and shopping cart management.</p>
                </div>
                
                <div className="cookie-type">
                  <h3>Analytics Cookies</h3>
                  <p>Help us understand how you use our service to improve user experience.</p>
                </div>
                
                <div className="cookie-type">
                  <h3>Marketing Cookies</h3>
                  <p>Used to show you relevant advertisements and promotional content.</p>
                </div>
              </div>
              
              <p>You can control cookie preferences through your browser settings or our cookie preference center.</p>
            </div>
          </div>

          {/* Contact Information */}
          <div className="privacy-section">
            <h2 className="section-title">Contact Us</h2>
            <div className="content-card">
              <p>If you have any questions about this Privacy Policy or our data practices, please contact us:</p>
              
              <div className="contact-info">
                <div className="contact-item">
                  <strong>Email:</strong> privacy@zepeat.com
                </div>
                <div className="contact-item">
                  <strong>Phone:</strong> +1 (555) 123-4567
                </div>
                <div className="contact-item">
                  <strong>Address:</strong> 123 Food Street, City Center, New York, NY 10001
                </div>
              </div>
            </div>
          </div>

          {/* Updates */}
          <div className="privacy-section">
            <h2 className="section-title">Policy Updates</h2>
            <div className="content-card">
              <p>
                We may update this Privacy Policy from time to time to reflect changes in our practices or applicable laws. 
                We will notify you of any material changes by posting the updated policy on our website and app, and by 
                sending you a notification if required by law. Your continued use of our services after any changes 
                constitutes acceptance of the updated Privacy Policy.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <Footer websiteType="customer" />
      <BottomTabNav />
    </div>
  );
};

export default CustomerPrivacyPolicy;
